/**
 * Pre-optimize address validation.
 *
 * A getRoutes stop carries its delivery address as a `***`-delimited string
 * (see normalizeOrder.buildAddress / orderUpdateFields):
 *
 *   Apt***StDr***Unit***CityTown***PostalCode***Country?  →  indices below
 *   0    1     2      3         4           5         6
 *   Apt  StDr  Unit   CityTown  PostalCode  Province  Country
 *
 * A deliverable address needs the street, city, postal code and province — Apt,
 * Unit and Country may legitimately be blank. The optimizer routes on geocodes,
 * so a stop also needs a usable lat/long: an order whose address couldn't be
 * geocoded lands at the (0,0) sentinel, which would send the solver to Null
 * Island. Both conditions gate an optimize run (block, don't silently route).
 */

/**
 * Required `***`-segment indexes: StDr, CityTown, PostalCode, Province.
 *
 * Same four fields as REQUIRED_ADDRESS_FIELDS in src/helpers/validateAddressInfo.ts,
 * which the entry forms enforce at save time. Change both together.
 */
const REQUIRED_ADDRESS_SEGMENTS = [1, 3, 4, 5]

/** A usable delivery geocode: numeric lat/long that isn't the (0,0) "un-geocoded" sentinel. */
export const hasUsableGeocode = (stop: any): boolean => {
  const g = stop?.geocodes
  if (!g || typeof g.lat !== 'number' || typeof g.long !== 'number') return false
  return !(g.lat === 0 && g.long === 0)
}

/** True when the stop has every required address field AND a usable geocode. */
export const stopHasCompleteAddress = (stop: any): boolean => {
  const parts = String(stop?.address ?? '').split('***')
  for (const i of REQUIRED_ADDRESS_SEGMENTS) {
    if (!String(parts[i] ?? '').trim()) return false
  }
  return hasUsableGeocode(stop)
}

export interface IncompleteAddressStop { id: string; invoice: string }

/**
 * Collect the non-HUB stops that would be sent to the solver but have an
 * incomplete address or unusable geocode.
 *
 * Skips LOCKED routes — a user-locked truck AND the artificially-locked PreRoute
 * bucket are left untouched by the backend, so they aren't being optimized. When
 * `scopeVehicles` is non-empty (single-vehicle optimize) only those vehicles'
 * stops are checked, matching the TSP scope.
 */
export const collectIncompleteAddressStops = (
  payloadRoutes: any[],
  scopeVehicles: string[] = [],
): IncompleteAddressStop[] => {
  const scope = scopeVehicles.length ? new Set(scopeVehicles) : null
  const bad: IncompleteAddressStop[] = []
  for (const r of payloadRoutes || []) {
    if (!r || r.isTruckLocked) continue
    if (scope && !scope.has(r.vehicle)) continue
    for (const s of r.stops || []) {
      if (!s || s.orderType === 'HUB') continue
      if (!stopHasCompleteAddress(s)) bad.push({ id: s.id, invoice: s.invoice || s.id })
    }
  }
  return bad
}

/** User-facing block message listing the offending invoices (capped). */
export const formatIncompleteAddressError = (offenders: IncompleteAddressStop[]): string => {
  const CAP = 8
  const invoices = offenders.map((o) => o.invoice)
  const shown = invoices.slice(0, CAP).join(', ')
  const more = invoices.length > CAP ? ` and ${invoices.length - CAP} more` : ''
  const n = offenders.length
  const has = n === 1 ? 'has' : 'have'
  const it = n === 1 ? 'it' : 'them'
  return `Cannot optimize — ${n} order${n === 1 ? '' : 's'} ${has} an incomplete or un-geocoded address: ${shown}${more}. Fix ${it} and try again.`
}
